import React, { useState, useEffect } from 'react';
import './DynamicTravelHistoryTable.css';

interface TravelEntry {
  fromDate: string;
  toDate: string;
  country: string;
  location: string;
  purpose: string;
  status: string;
}

interface DynamicTravelHistoryTableProps {
  value: { [key: string]: TravelEntry };
  onChange: (value: { [key: string]: TravelEntry }) => void;
  required?: boolean;
}

const DynamicTravelHistoryTable: React.FC<DynamicTravelHistoryTableProps> = ({
  value,
  onChange,
  required = false
}) => {
  const [entries, setEntries] = useState<{ [key: string]: TravelEntry }>(value || {});

  useEffect(() => {
    setEntries(value || {});
  }, [value]);

  // Add keyboard support for Ctrl+Enter to add new entry
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        addEntry();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const addEntry = () => {
    const entryCount = Object.keys(entries).length;
    const newKey = `travelEntry${entryCount + 1}`;
    
    const newEntry: TravelEntry = {
      fromDate: '',
      toDate: '',
      country: '',
      location: '',
      purpose: '',
      status: ''
    };
    
    const updatedEntries = { ...entries, [newKey]: newEntry };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (key: string) => {
    const { [key]: removed, ...remainingEntries } = entries;
    
    // Renumber the remaining entries
    const renumberedEntries: { [key: string]: TravelEntry } = {};
    const remainingKeys = Object.keys(remainingEntries).sort();
    
    remainingKeys.forEach((oldKey, index) => {
      const newKey = `travelEntry${index + 1}`;
      renumberedEntries[newKey] = remainingEntries[oldKey];
    });
    
    setEntries(renumberedEntries);
    onChange(renumberedEntries);
  };

  const updateEntry = (key: string, field: keyof TravelEntry, value: string) => {
    const updatedEntries = {
      ...entries,
      [key]: { ...entries[key], [field]: value }
    };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const entryKeys = Object.keys(entries).sort();

  return (
    <div className="dynamic-travel-history-table">
      <div className="table-header">
        <h4>Travel History for Last 10 Years</h4>
        <div className="header-actions">
          <button 
            type="button" 
            className="add-row-btn"
            onClick={addEntry}
            title="Add new travel entry (Ctrl+Enter)"
          >
            + Add Travel Entry
          </button>
        </div>
      </div>

      {entryKeys.length === 0 ? (
        <div className="empty-state">
          <p>No travel history entries added yet.</p>
          <button 
            type="button" 
            className="add-first-btn"
            onClick={addEntry}
          >
            Add First Travel Entry
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entryKeys.map((key, index) => (
            <div key={key} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>Travel Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(key)}
                  title="Remove this travel entry"
                >
                  ×
                </button>
              </div>
              
              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>From Date</label>
                    <input
                      type="text"
                      value={entries[key].fromDate}
                      onChange={(e) => updateEntry(key, 'fromDate', e.target.value)}
                      placeholder="e.g., 2020-01"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>To Date</label>
                    <input
                      type="text"
                      value={entries[key].toDate}
                      onChange={(e) => updateEntry(key, 'toDate', e.target.value)}
                      placeholder="e.g., 2020-03"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>Country</label>
                    <input
                      type="text"
                      value={entries[key].country}
                      onChange={(e) => updateEntry(key, 'country', e.target.value)}
                      placeholder="e.g., USA"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={entries[key].location}
                      onChange={(e) => updateEntry(key, 'location', e.target.value)}
                      placeholder="e.g., New York"
                    />
                  </div>
                </div>
                
                <div className="form-row form-row-2">
                  <div className="field-group">
                    <label>Purpose</label>
                    <select
                      value={entries[key].purpose}
                      onChange={(e) => updateEntry(key, 'purpose', e.target.value)}
                    >
                      <option value="">Select purpose</option>
                      <option value="Tourism">Tourism</option>
                      <option value="Business">Business</option>
                      <option value="Study">Study</option>
                      <option value="Work">Work</option>
                      <option value="Family Visit">Family Visit</option>
                      <option value="Medical">Medical</option>
                      <option value="Transit">Transit</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div className="field-group">
                    <label>Status</label>
                    <select
                      value={entries[key].status}
                      onChange={(e) => updateEntry(key, 'status', e.target.value)}
                    >
                      <option value="">Select status</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Pending">Pending</option>
                      <option value="Expired">Expired</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add another button at the bottom */}
      {entryKeys.length > 0 && (
        <div className="table-footer">
          <p className="add-hint">Need to add more travel entries?</p>
          <button 
            type="button" 
            className="add-another-btn"
            onClick={addEntry}
          >
            + Add Another Travel Entry
          </button>
        </div>
      )}

      {required && entryKeys.length === 0 && (
        <small className="error-text">At least one travel history entry is required</small>
      )}
    </div>
  );
};

export default DynamicTravelHistoryTable; 